import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useActivity, useUpdateActivity } from '@/hooks/use-activities';
import { createActivitySchema, type ActivityFormData } from '@fame/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';

export function EditActivityPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: activity, isLoading } = useActivity(id!);
  const updateActivity = useUpdateActivity();

  const form = useForm<ActivityFormData>({
    resolver: zodResolver(createActivitySchema) as Resolver<ActivityFormData>,
    defaultValues: {
      projectId: '',
      internalName: '',
      code: 0,
      price: 0,
      isActive: true,
    },
  });

  useEffect(() => {
    if (activity) {
      form.reset({
        projectId: activity.projectId,
        internalName: activity.internalName,
        code: Number(activity.code) || 0,
        price: Number(activity.price) || 0,
        isActive: activity.isActive,
      });
    }
  }, [activity, form]);

  const onSubmit = async (data: ActivityFormData) => {
    await updateActivity.mutateAsync({ id: id!, data });
    navigate(`/projecten/${activity?.projectId}`);
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Activiteit niet gevonden</p>
        <Button asChild variant="link">
          <Link to="/projecten">Terug naar projecten</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/projecten/${activity.projectId}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Activiteit Bewerken</h1>
          <p className="text-muted-foreground">{activity.internalName}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Activiteit Informatie</CardTitle>
          <CardDescription>Bewerk de gegevens van deze optionele activiteit</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="internalName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Interne Naam</FormLabel>
                    <FormControl>
                      <Input placeholder="bijv. Museum Visit" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" step="1" placeholder="bijv. 101" {...field} />
                    </FormControl>
                    <FormDescription>Unieke code voor dit project</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prijs</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0" placeholder="0,00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Actief</FormLabel>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" asChild>
                  <Link to={`/projecten/${activity.projectId}`}>Annuleren</Link>
                </Button>
                <Button type="submit" disabled={updateActivity.isPending}>
                  {updateActivity.isPending ? 'Bezig met opslaan...' : 'Opslaan'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
